import {
    CircleCheck,
    RotateCcw,
    UserRoundMinus,
    UserRoundPlus,
} from 'lucide-react';
import { type ReactElement } from 'react';

import { TimelineEventRow } from '@apps/web/components/ui/timeline_event_row';
import { formatTime } from '@apps/web/lib/date_utils';
import { type OrganizationConversationAuditEvent } from '@apps/web/swr_data_fetching/conversation_ownership';

type AuditTimelineItemProps = {
    readonly event: OrganizationConversationAuditEvent;
    readonly timezone: string;
};

// Builds the human sentence for a lifecycle/ownership audit event. A null
// actor means the system acted (e.g. an inbound message auto-reopening a
// closed conversation), rendered as "Phoebe".
export function auditEventSentence(
    event: OrganizationConversationAuditEvent,
): string {
    const actor = event.actor_user_name ?? 'Phoebe';
    const target = event.target_user_name ?? 'someone';
    switch (event.event_type) {
        case 'assigned':
            return `${actor} assigned this conversation to ${target}`;
        case 'unassigned':
            return `${actor} unassigned this conversation`;
        case 'closed':
            return `${actor} closed the conversation`;
        case 'reopened':
            return event.actor_user_name
                ? `${event.actor_user_name} reopened the conversation`
                : 'Reopened automatically';
        default: {
            const exhaustiveCheck: never = event.event_type;
            return exhaustiveCheck;
        }
    }
}

function auditEventIcon(
    eventType: OrganizationConversationAuditEvent['event_type'],
): ReactElement {
    switch (eventType) {
        case 'assigned':
            return <UserRoundPlus />;
        case 'unassigned':
            return <UserRoundMinus />;
        case 'closed':
            return <CircleCheck />;
        case 'reopened':
            return <RotateCcw />;
        default: {
            const exhaustiveCheck: never = eventType;
            return exhaustiveCheck;
        }
    }
}

export function AuditTimelineItem({
    event,
    timezone,
}: AuditTimelineItemProps): ReactElement {
    return (
        <TimelineEventRow
            className="my-3 justify-center"
            icon={auditEventIcon(event.event_type)}
        >
            <span className="min-w-0">{auditEventSentence(event)}</span>
            <span className="text-gray-400">·</span>
            <span className="tabular-nums">
                {formatTime(event.created_at, timezone)}
            </span>
        </TimelineEventRow>
    );
}
